import React, { ReactElement, ReactNode } from "react";
import { Field, Colors } from "@lib/shared/types";
import { DomainForm } from "src/api/domainForm";
import { DomainInfo } from "src/api/domainInfo";
import { useForm } from "../hooks/useForm";
import { Classes, FormProps } from "@components/shared/form";
import { InvalidFieldWithValue } from "../hooks";
import FormErrorsStep from "./FormErrorsStep";
import OtpStep from "./OtpStep";
import BuyersListStep from "./BuyersListStep";
import InlineThankYouStep from "./InlineThankYouStep";

type UseFormReturn = ReturnType<typeof useForm>;

export interface FormExtraStepsProps {
    // Errors step
    formErrors: UseFormReturn["formErrors"];
    formErrorsResolved: UseFormReturn["formErrorsResolved"];
    invalidFields: InvalidFieldWithValue[];
    isExpandable: (field: Field) => boolean;
    getExpandableFields: (field: Field) => Field[];
    domain: DomainInfo;
    domainNameTranslation: string;
    isLastStep: boolean;
    currentStep: number;

    // OTP step
    showOtp: boolean | undefined;
    otp: UseFormReturn["otp"];

    // Buyers list step
    dispatch: UseFormReturn["dispatch"];
    submitButtonText: string | undefined;
    startSecondRoundBidding: UseFormReturn["startSecondRoundBidding"];

    // Inline thank-you step
    formSubmitted: boolean;
    goToThankYouPage: boolean | undefined;
    goToListPage: boolean | undefined;
    compactOfferBlock: unknown;
    hideOffers: boolean | undefined;
    customThankYouStep: ReactNode | undefined;

    // Shared
    fieldsData: UseFormReturn["fieldsData"];
    finalForm: DomainForm;
    handleFieldBlur: UseFormReturn["handleFieldBlur"];
    handleFieldChange: UseFormReturn["handleFieldChange"];
    stepStatus: UseFormReturn["stepStatus"];
    isSecondServiceForm: boolean | undefined;
    formProps: FormProps;
    classes?: Classes;
    colors: Colors;
    isLoading: boolean;
    operation: UseFormReturn["operation"];
    searchableMultiSelect: boolean | undefined;
    singleQuestion: boolean | undefined;
}

/**
 * Renders the four "extra" virtual steps that live inside the steps wrapper
 * AFTER the real form steps: errors summary, OTP prompt, round-two buyers list,
 * and the inline thank-you screen. Each one renders a positioned slot and only
 * shows its content when its own condition matches.
 */
export default function FormExtraSteps(
    props: FormExtraStepsProps,
): ReactElement {
    const {
        formErrors,
        formErrorsResolved,
        invalidFields,
        isExpandable,
        getExpandableFields,
        domain,
        domainNameTranslation,
        isLastStep,
        currentStep,
        showOtp,
        otp,
        dispatch,
        submitButtonText,
        startSecondRoundBidding,
        formSubmitted,
        goToThankYouPage,
        goToListPage,
        compactOfferBlock,
        hideOffers,
        customThankYouStep,
        fieldsData,
        finalForm,
        handleFieldBlur,
        handleFieldChange,
        stepStatus,
        isSecondServiceForm,
        formProps,
        classes,
        colors,
        isLoading,
        operation,
        searchableMultiSelect,
        singleQuestion,
    } = props;

    return (
        <>
            <FormErrorsStep
                formErrors={formErrors}
                formErrorsResolved={formErrorsResolved}
                goToThankYouPage={goToThankYouPage}
                otp={otp}
                domain={domain}
                domainNameTranslation={domainNameTranslation}
                invalidFields={invalidFields}
                classes={classes}
                isExpandable={isExpandable}
                getExpandableFields={getExpandableFields}
                stepStatus={stepStatus}
                isSecondServiceForm={isSecondServiceForm}
                formProps={formProps}
                handleFieldBlur={handleFieldBlur}
                handleFieldChange={handleFieldChange}
                fieldsData={fieldsData}
                finalForm={finalForm}
                searchableMultiSelect={searchableMultiSelect}
                singleQuestion={singleQuestion}
                colors={colors}
                isLastStep={isLastStep}
                operation={operation}
                isLoading={isLoading}
                formSubmitted={formSubmitted}
                currentStep={currentStep}
            />
            <OtpStep
                showOtp={showOtp}
                fieldsData={fieldsData}
                colors={colors}
                classes={classes}
                isLoading={isLoading}
                operation={operation}
                otp={otp}
                handleFieldChange={handleFieldChange}
            />
            <BuyersListStep
                operation={operation}
                dispatch={dispatch}
                submitButtonText={submitButtonText}
                isLoading={isLoading}
                startSecondRoundBidding={startSecondRoundBidding}
                classes={classes}
            />
            <InlineThankYouStep
                formSubmitted={formSubmitted}
                goToThankYouPage={goToThankYouPage}
                goToListPage={goToListPage}
                compactOfferBlock={compactOfferBlock}
                hideOffers={hideOffers}
                customThankYouStep={customThankYouStep}
                classes={classes}
                form={finalForm}
            />
        </>
    );
}
